import React, { useState, useEffect } from 'react';
import { useFormBuilder } from '../../context/FormBuilderContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { 
  Plus, Trash2, GripVertical, Save, X, Eye, ArrowLeft, Settings,
  Type, AlignLeft, Hash, List, CheckSquare, Calendar, Star,
  ChevronDown, ChevronUp, Copy, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

const FIELD_TYPES = [
  { id: 'text', label: 'Short Text', icon: Type, description: 'Single line text input' },
  { id: 'paragraph', label: 'Paragraph', icon: AlignLeft, description: 'Multi-line text input' },
  { id: 'number', label: 'Number', icon: Hash, description: 'Numeric input with validation' },
  { id: 'radio', label: 'Single Choice', icon: List, description: 'Radio buttons for one selection' },
  { id: 'checkbox', label: 'Multiple Choice', icon: CheckSquare, description: 'Checkboxes for multiple selections' },
  { id: 'dropdown', label: 'Dropdown', icon: ChevronDown, description: 'Dropdown selection' },
  { id: 'date', label: 'Date', icon: Calendar, description: 'Date picker' },
  { id: 'rating', label: 'Rating', icon: Star, description: '1-5 star rating' }
];

const FormEditor = ({ formId, onSave, onCancel }) => {
  const { fetchForm, createForm, updateForm, currentForm, loading } = useFormBuilder();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    fields: []
  });
  const [editingField, setEditingField] = useState(null);
  const [showFieldSelector, setShowFieldSelector] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    if (formId) {
      loadForm();
    } else {
      // New form - initialize with default fields
      setFormData({
        title: '',
        description: '',
        fields: []
      });
    }
  }, [formId]);

  const loadForm = async () => {
    const form = await fetchForm(formId);
    if (form) {
      setFormData({
        title: form.title || '',
        description: form.description || '',
        fields: (form.fields || []).filter(f => !f.is_system_field)
      });
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Form title is required');
      return;
    }

    let result;
    if (formId) {
      result = await updateForm(formId, {
        title: formData.title,
        description: formData.description,
        fields: formData.fields
      });
    } else {
      result = await createForm({
        title: formData.title,
        description: formData.description,
        fields: formData.fields
      });
    }

    if (result.success) {
      toast.success(formId ? 'Form updated' : 'Form created');
      onSave();
    } else {
      toast.error(result.error);
    }
  };

  const addField = (type) => {
    const fieldType = FIELD_TYPES.find(t => t.id === type);
    const newField = {
      id: uuidv4(),
      type,
      label: `New ${fieldType?.label || 'Field'}`,
      placeholder: '',
      help_text: '',
      required: false,
      order: formData.fields.length,
      options: ['radio', 'checkbox', 'dropdown', 'multiselect'].includes(type) ? ['Option 1', 'Option 2'] : null,
      min_value: type === 'number' ? null : undefined,
      max_value: type === 'number' ? null : undefined,
      max_rating: type === 'rating' ? 5 : undefined,
      conditions: [],
      is_system_field: false
    };
    
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
    setShowFieldSelector(false);
    setEditingField(newField);
  };

  const updateField = (fieldId, updates) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map(f => f.id === fieldId ? { ...f, ...updates } : f)
    }));
    if (editingField?.id === fieldId) {
      setEditingField(prev => ({ ...prev, ...updates }));
    }
  };

  const deleteField = (fieldId) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.id !== fieldId)
    }));
    if (editingField?.id === fieldId) {
      setEditingField(null);
    }
  };

  const duplicateField = (field) => {
    const newField = {
      ...field,
      id: uuidv4(),
      label: `${field.label} (Copy)`,
      order: formData.fields.length
    };
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  const moveField = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= formData.fields.length) return;
    
    const newFields = [...formData.fields];
    const [moved] = newFields.splice(fromIndex, 1);
    newFields.splice(toIndex, 0, moved);
    
    // Update order
    newFields.forEach((f, i) => f.order = i);
    
    setFormData(prev => ({ ...prev, fields: newFields }));
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      moveField(draggedIndex, index);
      setDraggedIndex(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Get all fields for condition references
  const getFieldsForConditions = () => {
    return formData.fields.filter(f => 
      ['text', 'radio', 'checkbox', 'dropdown', 'number'].includes(f.type)
    );
  };

  const FieldTypeIcon = ({ type }) => {
    const fieldType = FIELD_TYPES.find(t => t.id === type);
    if (!fieldType) return <Type className="w-4 h-4" />;
    const Icon = fieldType.icon;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onCancel}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">
              {formId ? 'Edit Form' : 'Create New Form'}
            </h2>
            <p className="text-slate-500">Design your form fields and settings</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowPreview(true)}>
            <Eye className="w-4 h-4 mr-2" /> Preview
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            <Save className="w-4 h-4 mr-2" /> Save Form
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form Settings */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Form Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Form Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter form title"
                  data-testid="form-title"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter form description (optional)"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* System Fields Info */}
          <Card className="bg-slate-50 border-dashed">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Layers className="w-5 h-5 text-slate-500 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">System Fields</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Email ID and Name fields are automatically added to every form. 
                    Email is used to identify respondents and Name is auto-populated from employee records.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Add Field Button */}
          <Button 
            className="w-full" 
            onClick={() => setShowFieldSelector(true)}
            data-testid="add-field-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Field
          </Button>
        </div>

        {/* Form Fields */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Form Fields</CardTitle>
              <CardDescription>
                Drag to reorder fields. Click to edit.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* System Fields Preview */}
              <div className="space-y-2 mb-4 opacity-60">
                <div className="p-3 border border-dashed rounded-lg bg-slate-50">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">System</Badge>
                    <span className="font-medium text-sm">Email ID</span>
                    <Badge variant="outline" className="text-xs">Required</Badge>
                  </div>
                </div>
                <div className="p-3 border border-dashed rounded-lg bg-slate-50">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">System</Badge>
                    <span className="font-medium text-sm">Full Name</span>
                    <Badge variant="outline" className="text-xs">Auto-populated</Badge>
                  </div>
                </div>
              </div>

              <Separator className="my-4" />

              {/* Custom Fields */}
              {formData.fields.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Plus className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No custom fields yet</p>
                  <p className="text-sm">Click "Add Field" to start building your form</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {formData.fields.map((field, index) => (
                    <div
                      key={field.id}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        editingField?.id === field.id 
                          ? 'border-orange-500 bg-orange-50' 
                          : 'hover:border-slate-300 hover:bg-slate-50'
                      } ${draggedIndex === index ? 'opacity-50' : ''}`}
                      onClick={() => setEditingField(field)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <GripVertical className="w-4 h-4 text-slate-400 cursor-grab" />
                          <FieldTypeIcon type={field.type} />
                          <div>
                            <p className="font-medium text-sm">{field.label}</p>
                            <p className="text-xs text-slate-500">
                              {FIELD_TYPES.find(t => t.id === field.type)?.label}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {field.required && (
                            <Badge variant="outline" className="text-xs">Required</Badge>
                          )}
                          {field.conditions?.length > 0 && (
                            <Badge variant="secondary" className="text-xs">Conditional</Badge>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={(e) => { e.stopPropagation(); moveField(index, index - 1); }}
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={(e) => { e.stopPropagation(); moveField(index, index + 1); }}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={(e) => { e.stopPropagation(); duplicateField(field); }}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-red-500"
                            onClick={(e) => { e.stopPropagation(); deleteField(field.id); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Field Type Selector Dialog */}
      <Dialog open={showFieldSelector} onOpenChange={setShowFieldSelector}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Field</DialogTitle>
            <DialogDescription>Choose a field type to add to your form</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-4">
            {FIELD_TYPES.map(type => (
              <button
                key={type.id}
                onClick={() => addField(type.id)}
                className="p-4 border rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all text-left"
              >
                <type.icon className="w-6 h-6 text-orange-500 mb-2" />
                <p className="font-medium text-sm">{type.label}</p>
                <p className="text-xs text-slate-500 mt-1">{type.description}</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Field Editor Dialog */}
      <Dialog open={!!editingField} onOpenChange={(open) => !open && setEditingField(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Field</DialogTitle>
            <DialogDescription>Configure field properties and validation</DialogDescription>
          </DialogHeader>
          {editingField && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Field Label *</Label>
                <Input
                  value={editingField.label}
                  onChange={(e) => updateField(editingField.id, { label: e.target.value })}
                  placeholder="Enter field label"
                />
              </div>
              <div className="space-y-2">
                <Label>Placeholder Text</Label>
                <Input
                  value={editingField.placeholder || ''}
                  onChange={(e) => updateField(editingField.id, { placeholder: e.target.value })}
                  placeholder="Enter placeholder text"
                />
              </div>
              <div className="space-y-2">
                <Label>Help Text</Label>
                <Input
                  value={editingField.help_text || ''}
                  onChange={(e) => updateField(editingField.id, { help_text: e.target.value })}
                  placeholder="Additional instructions for respondents"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Required</Label>
                <Switch
                  checked={editingField.required}
                  onCheckedChange={(checked) => updateField(editingField.id, { required: checked })}
                />
              </div>

              {/* Options for selection fields */}
              {['radio', 'checkbox', 'dropdown', 'multiselect'].includes(editingField.type) && (
                <div className="space-y-2">
                  <Label>Options</Label>
                  {(editingField.options || []).map((option, i) => (
                    <div key={i} className="flex gap-2">
                      <Input
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...(editingField.options || [])];
                          newOptions[i] = e.target.value;
                          updateField(editingField.id, { options: newOptions });
                        }}
                        placeholder={`Option ${i + 1}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const newOptions = (editingField.options || []).filter((_, idx) => idx !== i);
                          updateField(editingField.id, { options: newOptions });
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newOptions = [...(editingField.options || []), `Option ${(editingField.options || []).length + 1}`];
                      updateField(editingField.id, { options: newOptions });
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Option
                  </Button>
                </div>
              )}

              {/* Number field validation */}
              {editingField.type === 'number' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Min Value</Label>
                    <Input
                      type="number"
                      value={editingField.min_value ?? ''}
                      onChange={(e) => updateField(editingField.id, { 
                        min_value: e.target.value ? Number(e.target.value) : null 
                      })}
                      placeholder="No minimum"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Value</Label>
                    <Input
                      type="number"
                      value={editingField.max_value ?? ''}
                      onChange={(e) => updateField(editingField.id, { 
                        max_value: e.target.value ? Number(e.target.value) : null 
                      })}
                      placeholder="No maximum"
                    />
                  </div>
                </div>
              )}

              {/* Conditional Logic */}
              <Separator />
              <div className="space-y-2">
                <Label>Conditional Visibility</Label>
                <p className="text-xs text-slate-500">
                  Show this field only when certain conditions are met
                </p>
                {(editingField.conditions || []).map((condition, i) => {
                  // Bug 1 Fix: Get the selected field to show its options
                  const selectedConditionField = formData.fields.find(f => f.id === condition.field_id);
                  const hasOptions = selectedConditionField && 
                    ['radio', 'checkbox', 'dropdown', 'multiselect'].includes(selectedConditionField.type) &&
                    selectedConditionField.options?.length > 0;
                  
                  return (
                    <div key={i} className="p-3 border rounded-lg space-y-3">
                      {i > 0 && (
                        <Select
                          value={condition.logic}
                          onValueChange={(v) => {
                            const newConditions = [...(editingField.conditions || [])];
                            newConditions[i] = { ...newConditions[i], logic: v };
                            updateField(editingField.id, { conditions: newConditions });
                          }}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AND">AND</SelectItem>
                            <SelectItem value="OR">OR</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      {/* Bug 2 Fix: Stack layout for better responsiveness */}
                      <div className="space-y-2">
                        <div className="flex gap-2 items-center">
                          <span className="text-sm text-slate-500 shrink-0">If</span>
                          <Select
                            value={condition.field_id}
                            onValueChange={(v) => {
                              const newConditions = [...(editingField.conditions || [])];
                              // Reset value when field changes
                              newConditions[i] = { ...newConditions[i], field_id: v, value: '' };
                              updateField(editingField.id, { conditions: newConditions });
                            }}
                          >
                            <SelectTrigger className="flex-1 min-w-0">
                              <SelectValue placeholder="Select field" />
                            </SelectTrigger>
                            <SelectContent className="max-w-[300px]">
                              {getFieldsForConditions()
                                .filter(f => f.id !== editingField.id)
                                .map(f => (
                                  <SelectItem key={f.id} value={f.id}>
                                    <span className="truncate block max-w-[250px]">{f.label}</span>
                                  </SelectItem>
                                ))
                              }
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="shrink-0"
                            onClick={() => {
                              const newConditions = (editingField.conditions || []).filter((_, idx) => idx !== i);
                              updateField(editingField.id, { conditions: newConditions });
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex gap-2 items-center">
                          <Select
                            value={condition.operator}
                            onValueChange={(v) => {
                              const newConditions = [...(editingField.conditions || [])];
                              newConditions[i] = { ...newConditions[i], operator: v };
                              updateField(editingField.id, { conditions: newConditions });
                            }}
                          >
                            <SelectTrigger className="w-32 shrink-0">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="equals">Equals</SelectItem>
                              <SelectItem value="not_equals">Not Equals</SelectItem>
                              <SelectItem value="contains">Contains</SelectItem>
                            </SelectContent>
                          </Select>
                          {/* Bug 1 Fix: Show dropdown for fields with options, otherwise show input */}
                          {hasOptions ? (
                            <Select
                              value={condition.value || ''}
                              onValueChange={(v) => {
                                const newConditions = [...(editingField.conditions || [])];
                                newConditions[i] = { ...newConditions[i], value: v };
                                updateField(editingField.id, { conditions: newConditions });
                              }}
                            >
                              <SelectTrigger className="flex-1 min-w-0">
                                <SelectValue placeholder="Select value" />
                              </SelectTrigger>
                              <SelectContent className="max-w-[250px]">
                                {selectedConditionField.options.map((opt, optIdx) => (
                                  <SelectItem key={optIdx} value={opt}>
                                    <span className="truncate block max-w-[200px]">{opt}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              className="flex-1 min-w-0"
                              value={condition.value || ''}
                              onChange={(e) => {
                                const newConditions = [...(editingField.conditions || [])];
                                newConditions[i] = { ...newConditions[i], value: e.target.value };
                                updateField(editingField.id, { conditions: newConditions });
                              }}
                              placeholder="Enter value"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newConditions = [...(editingField.conditions || []), {
                      field_id: '',
                      operator: 'equals',
                      value: '',
                      logic: 'AND'
                    }];
                    updateField(editingField.id, { conditions: newConditions });
                  }}
                  disabled={getFieldsForConditions().filter(f => f.id !== editingField.id).length === 0}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Condition
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setEditingField(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{formData.title || 'Untitled Form'}</DialogTitle>
            {formData.description && (
              <DialogDescription>{formData.description}</DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* System Fields */}
            <div className="space-y-2">
              <Label>Email ID <span className="text-red-500">*</span></Label>
              <Input placeholder="Enter your email address" disabled />
              <p className="text-xs text-slate-500">Your official email address</p>
            </div>
            <div className="space-y-2">
              <Label>Full Name <span className="text-red-500">*</span></Label>
              <Input placeholder="Your name will appear here" disabled className="bg-slate-50" />
              <p className="text-xs text-slate-500">Auto-populated from employee records</p>
            </div>
            
            {/* Custom Fields */}
            {formData.fields.map(field => (
              <div key={field.id} className="space-y-2">
                <Label>
                  {field.label} 
                  {field.required && <span className="text-red-500">*</span>}
                </Label>
                {field.type === 'text' && (
                  <Input placeholder={field.placeholder} disabled />
                )}
                {field.type === 'paragraph' && (
                  <Textarea placeholder={field.placeholder} disabled rows={3} />
                )}
                {field.type === 'number' && (
                  <Input type="number" placeholder={field.placeholder} disabled />
                )}
                {field.type === 'date' && (
                  <Input type="date" disabled />
                )}
                {field.type === 'rating' && (
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(star => (
                      <Star key={star} className="w-6 h-6 text-slate-300" />
                    ))}
                  </div>
                )}
                {['radio', 'dropdown'].includes(field.type) && (
                  <Select disabled>
                    <SelectTrigger>
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                  </Select>
                )}
                {['checkbox', 'multiselect'].includes(field.type) && (
                  <div className="space-y-2">
                    {(field.options || []).map((opt, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input type="checkbox" disabled className="rounded" />
                        <span className="text-sm">{opt}</span>
                      </div>
                    ))}
                  </div>
                )}
                {field.help_text && (
                  <p className="text-xs text-slate-500">{field.help_text}</p>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FormEditor;
